import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import VerifyEmail from './components/VerifyEmail';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import Dashboard from './components/Dashboard';
import Transactions from './components/Transactions';
import CashManagement from './components/CashManagement';
import Reports from './components/Reports';
import RecurringTransactions from './components/RecurringTransactions';
import ImportExport from './components/ImportExport';
import Settings from './components/Settings';
import DeleteAccount from './components/DeleteAccount';
import Layout from './components/Layout';
import StructuredData from './components/StructuredData';
import { apiService } from './services/api';
import { DEFAULT_FEATURE_FLAGS, FeatureFlags } from './types/featureFlags';

// Inner component to access navigate hook
function AppRoutes({
  isAuthenticated,
  setIsAuthenticated,
  featureFlags
}: {
  isAuthenticated: boolean | null;
  setIsAuthenticated: (value: boolean) => void;
  featureFlags: FeatureFlags;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    // Register global 401 handler - CRITICAL for account deletion and auth issues
    const handleUnauthorized = () => {
      console.log('Global 401 handler: Forcing logout and redirect');
      
      // Cancel all pending API requests
      apiService.cancelAllRequests();
      
      // Update auth state immediately
      setIsAuthenticated(false);
      
      // Redirect to login immediately
      navigate('/login', { replace: true });
    };

    apiService.setUnauthorizedCallback(handleUnauthorized);

    // Cleanup on unmount
    return () => {
      apiService.clearUnauthorizedCallback();
    };
  }, [navigate, setIsAuthenticated]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/app" replace /> : <LandingPage />} 
      />
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/app" replace /> : <Login onLoginSuccess={() => setIsAuthenticated(true)} />} 
      />
      <Route 
        path="/register" 
        element={isAuthenticated ? <Navigate to="/app" replace /> : <Register />} 
      />
      <Route 
        path="/forgot-password" 
        element={isAuthenticated ? <Navigate to="/app" replace /> : <ForgotPassword />} 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmail />} 
      />
      <Route 
        path="/account/delete" 
        element={<DeleteAccount />} 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin/login" 
        element={<AdminLogin />} 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/app" 
        element={
          isAuthenticated ? (
            <Layout
              onLogout={() => setIsAuthenticated(false)}
              recurringTransactionsEnabled={featureFlags.recurringTransactions}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard recurringTransactionsEnabled={featureFlags.recurringTransactions} />} />
        <Route path="transactions" element={<Transactions />} />
        <Route
          path="recurring"
          element={
            featureFlags.recurringTransactions
              ? <RecurringTransactions />
              : <Navigate to="/app/dashboard" replace />
          }
        />
        <Route path="cash" element={<CashManagement />} />
        <Route path="reports" element={<Reports />} />
        <Route path="import-export" element={<ImportExport />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [loadedFlagsToken, setLoadedFlagsToken] = useState<string | null>(null);

  useEffect(() => {
    // Check authentication status on app load
    const checkAuth = () => {
      const authenticated = apiService.refreshAuthState();
      setIsAuthenticated(authenticated);
    };

    checkAuth();

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'rememberMe') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadFeatureFlags = async () => {
      if (!isAuthenticated) {
        setFeatureFlags(DEFAULT_FEATURE_FLAGS);
        setLoadedFlagsToken(null);
        return;
      }

      const currentToken = apiService.getToken();
      if (currentToken && loadedFlagsToken === currentToken) {
        return;
      }

      try {
        const flags = await apiService.getFeatureFlags();
        if (!isCancelled) {
          setFeatureFlags({
            recurringTransactions: !!flags.recurringTransactions,
          });
          setLoadedFlagsToken(currentToken);
        }
      } catch {
        if (!isCancelled) {
          setFeatureFlags(DEFAULT_FEATURE_FLAGS);
          setLoadedFlagsToken(currentToken);
        }
      }
    };

    loadFeatureFlags();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, loadedFlagsToken]);

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <StructuredData />
        <AppRoutes 
          isAuthenticated={isAuthenticated} 
          setIsAuthenticated={setIsAuthenticated}
          featureFlags={featureFlags}
        />
      </div>
    </Router>
  );
}

export default App;
